import { motion, type Variants } from "motion/react";
import { memo } from "react";

const TITLE_SCRAMBLE_START_DELAY_MS = 120;
const TITLE_SCRAMBLE_SPEED_MS = 55;
const MIN_CONTENT_REVEAL_DELAY_MS = 620;
const MAX_CONTENT_REVEAL_DELAY_MS = 1100;
const NON_SCRAMBLE_CONTENT_REVEAL_DELAY_MS = 140;
const CONTENT_ITEM_STAGGER_MS = 55;

export function getTitleContentRevealDelayMs(
  title: string,
  scrambleTitle = false,
): number {
  if (!scrambleTitle) {
    return NON_SCRAMBLE_CONTENT_REVEAL_DELAY_MS;
  }

  const almostCompleteDelay =
    TITLE_SCRAMBLE_START_DELAY_MS +
    title.length * TITLE_SCRAMBLE_SPEED_MS * 0.72;

  return Math.min(
    MAX_CONTENT_REVEAL_DELAY_MS,
    Math.max(MIN_CONTENT_REVEAL_DELAY_MS, almostCompleteDelay),
  );
}

export function getStaggeredContentRevealDelayMs(
  title: string,
  index: number,
  scrambleTitle = false,
): number {
  return (
    getTitleContentRevealDelayMs(title, scrambleTitle) +
    index * CONTENT_ITEM_STAGGER_MS
  );
}

const containerVariants: Variants = {
  hidden: {},
  visible: (delayChildrenMs = 0) => ({
    transition: {
      staggerChildren: 0.055,
      delayChildren: delayChildrenMs / 1000,
    },
  }),
  exit: { transition: { staggerChildren: 0.04, staggerDirection: -1 } },
};

const itemVariants: Variants = {
  hidden: { opacity: 0, x: 18, filter: "blur(4px)" },
  visible: (delayMs = 0) => ({
    opacity: 1,
    x: 0,
    filter: "blur(0px)",
    transition: { duration: 0.22, ease: "easeOut", delay: delayMs / 1000 },
  }),
  exit: {
    opacity: 0,
    x: -12,
    filter: "blur(4px)",
    transition: { duration: 0.14, ease: "easeIn" },
  },
};

export const StepMotionGroup = memo(function StepMotionGroup({
  animateItems = true,
  children,
  delayChildrenMs = 20,
}: {
  animateItems?: boolean;
  children: React.ReactNode;
  delayChildrenMs?: number;
}) {
  return (
    <motion.div
      animate="visible"
      custom={delayChildrenMs}
      exit={animateItems ? "exit" : undefined}
      initial={animateItems ? "hidden" : false}
      variants={animateItems ? containerVariants : undefined}
    >
      {children}
    </motion.div>
  );
});

export const StepMotionItem = memo(function StepMotionItem({
  animateItems = true,
  children,
  delayMs = 0,
}: {
  animateItems?: boolean;
  children: React.ReactNode;
  delayMs?: number;
}) {
  return (
    <motion.div
      custom={delayMs}
      variants={animateItems ? itemVariants : undefined}
    >
      {children}
    </motion.div>
  );
});
